import { useEffect, useState } from "react";
import { Trash2, Check, Save } from "lucide-react";
import toast from "react-hot-toast";
import { useStateMachineValue } from "@/core/arch/state_machine";
import { BUILT_IN_PRESETS } from "@cpos/shared/src/dev-merchant/presets";
import type {
  CreateMerchantBloc,
  SavedPreset,
} from "@/application/tools/create_merchant_bloc";
import { Button } from "@/components/ui/base/button";
import { Badge } from "@/components/ui/base/badge";
import { SavePresetDialog } from "../SavePresetDialog";
import { SectionCard } from "./shared";

interface Props {
  bloc: CreateMerchantBloc;
  disabled?: boolean;
}

function SavedPresetChip({
  preset,
  disabled,
  confirming,
  onSelect,
  onDeleteClick,
  onCancelDelete,
}: {
  preset: SavedPreset;
  disabled?: boolean;
  confirming: boolean;
  onSelect: () => void;
  onDeleteClick: () => void;
  onCancelDelete: () => void;
}) {
  return (
    <div className="border-border bg-muted flex items-center gap-1 rounded-md border py-1.5 pr-1 pl-2">
      <Badge variant="secondary" className="shrink-0" aria-hidden>
        👤
      </Badge>
      <button
        type="button"
        onClick={onSelect}
        disabled={disabled}
        title={preset.description ?? undefined}
        className="text-foreground text-xs font-medium disabled:cursor-not-allowed disabled:opacity-40"
      >
        {preset.name}
      </button>
      {confirming ? (
        <>
          <button
            type="button"
            onClick={onDeleteClick}
            title="Confirm delete"
            className="rounded p-1 text-red-600 hover:bg-red-50"
          >
            <Check className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={onCancelDelete}
            title="Cancel"
            className="text-subtle-foreground hover:text-foreground px-1 text-xs"
          >
            cancel
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={onDeleteClick}
          disabled={disabled}
          title="Delete preset"
          className="text-subtle-foreground rounded p-1 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export function PresetPicker({ bloc, disabled }: Props) {
  const [state] = useStateMachineValue(bloc);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    void bloc.loadPresets();
  }, [bloc]);

  async function handleDelete(id: string) {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      return;
    }
    setConfirmDeleteId(null);
    try {
      await bloc.deletePreset(id);
      toast.success("Preset deleted");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    }
  }

  return (
    <SectionCard title="Presets">
      <div className="space-y-4">
        <div>
          <p className="text-muted-foreground mb-2 text-xs font-medium">
            Built-in
          </p>
          <div className="flex flex-wrap gap-2">
            {BUILT_IN_PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                onClick={() => bloc.loadBuiltInPreset(preset.key)}
                disabled={disabled}
                title={preset.description}
                className="border-border bg-muted text-foreground hover:border-muted-foreground/40 hover:bg-card flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Badge variant="secondary" className="shrink-0" aria-hidden>
                  📦
                </Badge>
                {preset.name}
              </button>
            ))}
          </div>
        </div>

        {state.savedPresets.length > 0 && (
          <div>
            <p className="text-muted-foreground mb-2 text-xs font-medium">
              Saved
            </p>
            <div className="flex flex-wrap gap-2">
              {state.savedPresets.map((preset) => (
                <SavedPresetChip
                  key={preset.id}
                  preset={preset}
                  disabled={disabled}
                  confirming={confirmDeleteId === preset.id}
                  onSelect={() => bloc.loadSavedPreset(preset)}
                  onDeleteClick={() => void handleDelete(preset.id)}
                  onCancelDelete={() => setConfirmDeleteId(null)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="border-border border-t pt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSaveDialogOpen(true)}
            disabled={disabled}
          >
            <Save className="h-3.5 w-3.5" />
            Save as preset
          </Button>
        </div>
      </div>

      <SavePresetDialog
        bloc={bloc}
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
      />
    </SectionCard>
  );
}
